import { createSlice } from "@reduxjs/toolkit";

const emptyLineItems = {
  id: 0,
  product: null,
  type_of_service: null,
  quantity: 0,
  price: null,
  approximate_time: null,
  type_of_services: [],
};

const baseState = {
  schedule_step: 1,
  line_items: [emptyLineItems],
  total_vehicles: 0,
  total_price: 0,
  total_time: 0,
  tech: {
    verification_key: null,
    job_datetime: null,
    job_address: null,
    date: null,
    time: null,
    technician_id: null,
    lat: null,
    lon: null,
    description: null,
    tracking_number: null,
    purchase_order: null,
  },
  sendData: {
    verification_key: null,
    job_datetime: null,
    job_address: null,
    technician_id: null,
    lat: null,
    lon: null,
    has_equipment: false,
    payment_method_id: null,
    payment_brand: null,
    credit_to_use: 0,
    payments: null,
    verification_text: null,
    affirm_due_days: null,
    description: null,
    description_more: null,
    files: null,
    vehicle_files: null,
    job_items: null,
    primary_contact: null,
    location: null,
    vehicle_count: null,
    tracking_number: null,
    equipment_arrival_date: null,
    job_timezone: '',
    purchase_order: null,
  },
  equipment_arrival_date: null,
  disabledDates: null,
  allDays: null,
  hoursWork: null,
  technicians_for_job: null,
  radius: 150,
  rescheduleConfirmModal: false
};

const initialState = {
  schedule_step: 1,
  line_items: [emptyLineItems],
  total_vehicles: 0,
  total_price: 0,
  total_time: 0,
  tech: {
    verification_key: null,
    job_datetime: null,
    job_address: null,
    date: null,
    time: null,
    technician_id: null,
    lat: null,
    lon: null,
    description: null,
    tracking_number: null,
    purchase_order: null,
  },
  sendData: {
    verification_key: null,
    job_datetime: null,
    job_address: null,
    technician_id: null,
    lat: null,
    lon: null,
    has_equipment: false,
    payment_method_id: null,
    payment_brand: null,
    credit_to_use: 0,
    description: null,
    description_more: null,
    files: null,
    vehicle_files: null,
    job_items: null,
    primary_contact: null,
    location: null,
    vehicle_count: null,
    tracking_number: null,
    equipment_arrival_date: null,
    verification_text: null,
    affirm_due_days: null,
    job_timezone: '',
    purchase_order: null,
  },
  equipment_arrival_date: null,
  disabledDates: null,
  allDays: null,
  hoursWork: null,
  technician_locations: null,
  technicians_for_job: null,
  radius: 150,
  rescheduleConfirmModal: false
};

const scheduleSlice = createSlice({
  name: "schedule",
  initialState,
  reducers: {
    setScheduleStep(state, action) {
      state.schedule_step = action.payload;
    },
    changeSchedule(state, action) {
      state.line_items = state.line_items?.map((item) => {
        if (item.id === action.payload?.id) {
          item = { ...item, ...action.payload };

          if (action?.payload?.type === "product") {
            item = {
              ...item,
              type_of_service: null,
              approximate_time: null,
              price: null,
              is_main: null,
              quantity: 1,
            };
          }
          if (action?.payload?.data?.time) {
            item = {
              ...item,
              approximate_time: action?.payload?.data?.time,
              price: action?.payload?.data?.price,
              quantity: 1,
              is_main: action?.payload?.data?.is_main
            };
          }
        }

        return item;
      });
    },
    setQuantity(state, action) {
      state.line_items = state?.line_items?.map((item) => {
        if (item?.id === action?.payload?.id) {
          item = {
            ...item,
            price:
              (item?.type_of_service?.price ?? 0) * action?.payload?.quantity,
            approximate_time:
              (item?.type_of_service?.time ?? 0) * action?.payload?.quantity,
            quantity: action?.payload?.quantity,
          };
        }
        return item;
      });
    },
    setTotalVehicles(state, action) {
      state.total_vehicles = state?.line_items?.reduce(
        (sum, obj) => obj.is_main ? sum + obj.quantity : sum,
        0
      );
    },
    setTotalPrice(state, action) {
      state.total_price = state?.line_items?.reduce(
        (sum, obj) => Number(sum) + (Number(obj?.price) || 0),
        0
      );
    },
    setTotalTime(state, action) {
      state.total_time = state?.line_items?.reduce(
        (sum, obj) => Number(sum) + Number(obj?.approximate_time),
        0
      );
    },
    setTotalTimeAction(state, action) {
      state.total_time = action.payload;
    },
    setTotalPriceAction(state, action) {
      state.total_price = action.payload;
    },
    setTypeOfServices(state, action) {
      const filteredOptions = action?.payload?.options.filter(
        (obj1) =>
          !state?.line_items?.some(
            (obj2) => obj1.id === obj2.type_of_service?.id
          )
      );
      state.line_items = state?.line_items?.map((item) => {
        if (item?.id === action?.payload?.id) {
          item = { ...item, type_of_services: filteredOptions };
        }
        return item;
      });
    },
    addLineItems(state, action) {
      const id = state.line_items[state.line_items.length - 1]?.id + 1;
      state.line_items = [
        ...state.line_items,
        {
          ...emptyLineItems,
          id,
        },
      ];
    },
    removeLineItems(state, action) {
      state.line_items = state.line_items.filter(
        (item) => item.id !== action.payload
      );
    },
    incrementRadius(state, action){
      state.radius = action.payload;
    },
    decrementRadius(state, action){
      state.radius = action.payload;
    },
    setQtyRadius(state, action){
      state.radius = action.payload;
    },
    setTechnician(state, action) {
      state.tech = { ...state.tech, ...action?.payload };
    },
    setSendData(state, action) {
      state.sendData = { ...state.sendData, ...action?.payload };
    },
    setDisabledDates(state, action) {
      state.disabledDates = action?.payload;
    },
    setAllDays(state, action) {
      state.allDays = action?.payload;
    },
    setHoursWork(state, action) {
      state.hoursWork = action?.payload;
    },
    setTechnicianLocations(state, action) {
      state.technician_locations = action.payload;
    },
    setTechniciansForJob(state, action) {
      state.technicians_for_job = action?.payload;
    },
    setEquipmentArrivalDate(state, action) {
      state.equipment_arrival_date = action.payload;
    },
    clearState(state, action) {
      state.sendData = baseState?.sendData;
      state.schedule_step = baseState?.schedule_step;
      state.line_items = baseState?.line_items;
      state.total_vehicles = baseState?.total_vehicles;
      state.total_price = baseState?.total_price;
      state.total_time = baseState?.total_time;
      state.tech = baseState?.tech;
      state.equipment_arrival_date = baseState?.equipment_arrival_date;
      state.disabledDates = baseState?.disabledDates;
      state.allDays = baseState?.allDays;
      state.hoursWork = baseState?.hoursWork;
      state.technicians_for_job = baseState?.technicians_for_job;
      state.radius = baseState?.radius;
      state.has_equipment = baseState?.has_equipment;
      state.primary_contact = baseState?.primary_contact;
    },
    setNewLineItems(state, action) {
      state.line_items = action?.payload?.new_line_items;
    },
    setRescheduleConfirmModal(state, action) {
      state.rescheduleConfirmModal = action?.payload;
    },
  },
});
export const {
  setScheduleStep,
  addLineItems,
  removeLineItems,
  changeSchedule,
  setTypeOfServices,
  setQuantity,
  setTotalVehicles,
  setTotalPrice,
  setTotalTime,
  setTechnician,
  setSendData,
  setDisabledDates,
  setAllDays,
  setHoursWork,
  setTechniciansForJob,
  setEquipmentArrivalDate,
  clearState,
  setTechnicianLocations,
  incrementRadius,
  decrementRadius,
  setQtyRadius,
  setNewLineItems,
  setRescheduleConfirmModal,
  setTotalTimeAction,
  setTotalPriceAction
} = scheduleSlice.actions;

export default scheduleSlice.reducer;
